import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface GazeAccuracyChipProps {
  accuracy: number;
  onRecalibrate: () => void;
  onHelp: () => void;
  className?: string;
}

/**
 * Compact exam-chrome chip: "Accuracy | N%" with Recalibrate / Help menu.
 */
export function GazeAccuracyChip({
  accuracy,
  onRecalibrate,
  onHelp,
  className,
}: GazeAccuracyChipProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        type="button"
        className={cn(
          'inline-flex min-h-11 items-center gap-1.5 rounded-control border border-hairline',
          'bg-surface-card px-3 py-2 text-sm font-medium text-content-primary',
          'hover:border-ink hover:bg-stone focus:outline-none focus-visible:ring-2',
          'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-parchment'
        )}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="text-content-muted">Accuracy</span>
        <span className="text-content-muted" aria-hidden="true">
          |
        </span>
        <span className="tabular-nums">{Math.round(accuracy)}%</span>
        <ChevronDown className="size-4 text-content-muted" aria-hidden="true" />
      </button>

      {menuOpen && (
        <div
          id={menuId}
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-[10rem] rounded-control border border-subtle bg-paper py-1 shadow-elevated"
        >
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-sm text-content-primary hover:bg-stone"
            onClick={() => {
              setMenuOpen(false);
              onRecalibrate();
            }}
          >
            Recalibrate
          </button>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-sm text-content-primary hover:bg-stone"
            onClick={() => {
              setMenuOpen(false);
              onHelp();
            }}
          >
            Help
          </button>
        </div>
      )}
    </div>
  );
}
